import { Link } from 'react-router-dom'
import { api } from '../../lib/api.js'
import { Badge, Card, Empty, ErrorBox, Page, Spinner, useAsync } from '../../components/Ui.jsx'
import { FLAG, diffDays, fmtDate, inr, lakh, todayISO } from '../../lib/format.js'

function Queue ({ title, hint, items, alert, render }) {
  return (
    <div className="queue">
      <h3 className={alert && items.length ? 'alert' : ''}>
        {title}
        <span className="count">{items.length}</span>
      </h3>
      {items.length
        ? <ul>{items.map(o => <li key={o._id}>{render(o)}</li>)}</ul>
        : <div className="empty" style={{ padding: '18px 14px' }}>{hint}</div>}
    </div>
  )
}

const row = (l1, code, l2) => (
  <>
    <div className="l1">{l1}<span className="code">{code}</span></div>
    <div className="l2">{l2}</div>
  </>
)

export default function Desk () {
  const { data, error, loading, reload } = useAsync(() => api.get('/operator/desk'), [],
    { live: ['orders', 'batches', 'enquiries', 'trips'] })

  if (loading) return <Page title="Operator desk"><Spinner /></Page>
  if (error) return <Page title="Operator desk"><ErrorBox error={error} onRetry={reload} /></Page>

  const { queues: q, flaggedBatches, stockingSoon, activeTrips, settings, today, newEnquiries = [] } = data
  const openCount = Object.values(q).reduce((s, arr) => s + arr.length, 0)

  return (
    <Page
      title="Operator desk"
      subtitle={`Everything waiting on a human decision today (${fmtDate(today)}). Nothing in this platform moves on its own — money is verified by hand and survival results come in over the phone.`}
      actions={<>
        <Link className="btn primary" to="/operator/match">Farmer on the phone → match</Link>
        <button className="btn" onClick={reload}>Refresh</button>
      </>}
    >
      {newEnquiries.length > 0 && (
        <div className="alert info">
          <div>
            <b>{newEnquiries.length} new farmer enquir{newEnquiries.length === 1 ? 'y' : 'ies'} from the public directory</b>
            {newEnquiries.slice(0, 3).map(e => e.name).join(', ')}
            {newEnquiries.length > 3 ? ` and ${newEnquiries.length - 3} more` : ''}{newEnquiries.length === 1 ? ' is' : ' are'} waiting for a call back.{' '}
            <Link to="/operator/enquiries">Open the enquiry list →</Link>
          </div>
        </div>
      )}

      <div className="grid four" style={{ marginBottom: 18 }}>
        <div className="stat"><div className="n">{openCount}</div><div className="k">Items in your queues</div></div>
        <div className={`stat ${q.failedSamples.length ? 'alert' : ''}`}>
          <div className="n">{q.failedSamples.length}</div>
          <div className="k">Failed samples to re-match</div>
          <div className="sub">farmer still needs seed on the same date</div>
        </div>
        <div className={`stat ${flaggedBatches.some(b => b.qualityFlag === 'red_flag') ? 'alert' : flaggedBatches.length ? 'warn' : ''}`}>
          <div className="n">{flaggedBatches.length}</div>
          <div className="k">Batches flagged</div>
          <div className="sub">
            {(() => {
              const n = flaggedBatches.filter(b => b.qualityFlag === 'red_flag').length
              return `${n} need${n === 1 ? 's' : ''} a decision`
            })()}
          </div>
        </div>
        <div className="stat"><div className="n">{activeTrips.length}</div><div className="k">Vehicles on the road</div></div>
      </div>

      {flaggedBatches.filter(b => b.qualityFlag === 'red_flag' && !b.override?.at).map(b => (
        <div className="alert error" key={b._id}>
          <div>
            <b>RED FLAG · {b.batchCode} at {b.hatchery?.name}</b>
            {b.flagNote} <Link to={`/operator/batches/${b._id}/flag`}>Open the decision screen →</Link>
          </div>
        </div>
      ))}

      <div className="grid three" style={{ marginBottom: 18 }}>
        <Queue
          title="1 · Advance not received"
          hint="No bookings waiting on an advance."
          items={q.awaitingAdvance}
          render={o => (
            <Link to={`/operator/orders/${o._id}`}>
              {row(o.farmer?.name, o.orderCode,
                `${lakh(o.quantityLakh)} · advance ${inr(o.advanceAmount)} · stocking ${fmtDate(o.targetStockingDate)}`)}
            </Link>
          )}
        />
        <Queue
          title="2 · Sample balloon to dispatch"
          hint="No samples pending dispatch."
          items={q.sampleToDispatch}
          render={o => {
            const due = diffDays(todayISO(), o.sampleDueDate)
            return (
              <Link to={`/operator/orders/${o._id}`}>
                {row(o.farmer?.name, o.orderCode,
                  `${o.hatchery?.name} · sample due ${fmtDate(o.sampleDueDate)} ${due < 0 ? '(overdue)' : due === 0 ? '(today)' : `(in ${due}d)`}`)}
              </Link>
            )
          }}
        />
        <Queue
          title="3 · Survival result awaited"
          hint="No samples out for testing."
          items={q.awaitingResult}
          render={o => (
            <Link to={`/operator/orders/${o._id}`}>
              {row(o.farmer?.name, o.orderCode,
                `${o.farmer?.phone} · call for the 24-hour count · ${settings.pass_threshold_pct}% threshold`)}
            </Link>
          )}
        />
        <Queue
          title="4 · Balance to collect"
          hint="No balances outstanding."
          items={q.awaitingBalance}
          render={o => (
            <Link to={`/operator/orders/${o._id}`}>
              {row(o.farmer?.name, o.orderCode,
                `${inr(o.totalAmount - o.advanceAmount)} outstanding of ${inr(o.totalAmount)}`)}
            </Link>
          )}
        />
        <Queue
          title="5 · Hatchery payout to release"
          hint="No payouts pending."
          items={q.toPayout}
          render={o => (
            <Link to={`/operator/orders/${o._id}`}>
              {row(o.hatchery?.name, o.orderCode,
                `${inr(o.totalAmount)} received · release less ${o.commissionPct}% commission`)}
            </Link>
          )}
        />
        <Queue
          title="6 · Vehicle & liaison to assign"
          hint="Nothing waiting on transport."
          items={q.toDispatch}
          render={o => (
            <Link to={`/operator/orders/${o._id}`}>
              {row(o.farmer?.name, o.orderCode,
                `${o.hatchery?.name} → ${o.farmer?.village} · ${o.distanceKm} km`)}
            </Link>
          )}
        />
      </div>

      <div className="split">
        <Card title="Failed samples — farmers still without seed" padded={false}>
          <div className="scroll-x">
            <table>
              <thead>
                <tr><th>Order</th><th>Farmer</th><th className="num">Survival</th><th>Stocking date</th><th /></tr>
              </thead>
              <tbody>
                {q.failedSamples.map(o => (
                  <tr key={o._id}>
                    <td className="mono">{o.orderCode}<span className="t-sub">{o.hatchery?.name}</span></td>
                    <td>{o.farmer?.name}<span className="t-sub">{o.farmer?.phone}</span></td>
                    <td className="num">{o.sample?.survivalPct}%<span className="t-sub">{o.sample?.survivors}/{o.sample?.animalsSent}</span></td>
                    <td className="tight">{fmtDate(o.targetStockingDate)}</td>
                    <td className="right">
                      <Link className="btn sm primary" to={`/operator/match?rematchFrom=${o._id}`}>Re-match</Link>
                    </td>
                  </tr>
                ))}
                {!q.failedSamples.length && <tr><td colSpan={5}><Empty>No failed samples outstanding.</Empty></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Stocking in the next three days" padded={false}>
          <div className="scroll-x">
            <table>
              <tbody>
                {stockingSoon.map(o => (
                  <tr key={o._id}>
                    <td>
                      <Link className="rowlink" to={`/operator/orders/${o._id}`}>{o.farmer?.name}</Link>
                      <span className="t-sub">{o.hatchery?.name} · {lakh(o.quantityLakh)}</span>
                    </td>
                    <td className="right tight">
                      {fmtDate(o.targetStockingDate)}
                      <span className="t-sub">{o.status.replace(/_/g, ' ')}</span>
                    </td>
                  </tr>
                ))}
                {!stockingSoon.length && <tr><td><Empty>Nothing stocking in the next three days.</Empty></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {flaggedBatches.length > 0 && (
        <Card title="Batches under observation or red flag" padded={false}>
          <div className="scroll-x">
            <table>
              <thead>
                <tr><th>Batch</th><th>Hatchery</th><th>Flag</th><th className="num">Pass / fail</th><th>Note</th><th /></tr>
              </thead>
              <tbody>
                {flaggedBatches.map(b => (
                  <tr key={b._id}>
                    <td className="mono">{b.batchCode}<span className="t-sub">ready {fmtDate(b.readyDate)}</span></td>
                    <td>{b.hatchery?.name}</td>
                    <td>
                      <Badge tone={FLAG[b.qualityFlag].tone}>{FLAG[b.qualityFlag].label}</Badge>
                      {b.override?.at && <span className="t-sub">overridden by operator</span>}
                    </td>
                    <td className="num">{b.samplePassCount} / {b.sampleFailCount}</td>
                    <td className="small">{b.flagNote}</td>
                    <td className="right"><Link className="btn sm" to={`/operator/batches/${b._id}/flag`}>Decide</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </Page>
  )
}
